import React, { useEffect } from "react";
import { Form, Row, Col, Container } from "react-bootstrap";
import PropTypes from 'prop-types';
import { object, string, mixed, array as yuparray } from 'yup';
import { FormControl, FormLabel, makeStyles, Button, Card, CardContent } from '@material-ui/core';
import { CheckboxInput, TextInput, SelectInput, StyledRadioInput } from "../../../Helpers/Inputs";
import InsuranceProviders from '../../../Helpers/Lists/InsuranceProviders';
import { FLORIDA, MASSACHUSETTS, WYOMING } from '../../../Helpers/Constants/States';
import { NumberFormatDollars, InputMaskDate } from '../../../Helpers/Formatters';
import { ConvertStringToBoolean, ConvertBooleanToString } from "../../../Helpers/Formatters/ConvertBoolean";
import { emptyStringToUndefined } from '../../../Helpers/Formatters/EmptyStringToUndefined.js';
import Step7Icon from '../icons/step_7.svg';
import { isBefore, isAfter, isValid } from 'date-fns';
import IconHolder from "../../../Helpers/IconHolder";

const stateList = ["AK", "AL", "AR", "AZ", "CO", "CT", "DE", "GA", "HI", "IA", "ID", "IL", "IN", "KY", "LA", "MD", "ME", "MN", "MO", "MS", "MT", "NC", "NE", "NH", "NJ", "NM", "OH", "OR", "RI", "SC", "SD", "TN", "TX", "UT", "VA", "VT", "WI", "WV"];
const excludeReplacingReasonStates = ["CA", "DC", "FL", "KS", "MA", "MI", "ND", "NV", "OK", "PA", "WA", "WY"];

const useStyles = makeStyles(() => ({
  labelFont: {
    '&.MuiFormLabel-root': {
      color: '#212529',
      lineHeight: '1.5',
    },
  },
  multiLine: {
    width: 'max-content',
  },
  setCheckBox: {
    alignItems: 'start',
  },
  setMargin: {
    marginTop: '-7px',
  }
}));

export default function PolicyPurposeConfirmation(props) {

  const { register, errors, setValue, triggerValidation, reset } = props;

  const classes = useStyles();
  const state = props.fullFormState.steps.basicInfo && props.fullFormState.steps.basicInfo.state;
  // manually call setValue for fields that don't play nice with react-hook-form register
  const handleValueChange = (field, value) => {
    setValue(field, value);
    props.onFieldChange(field, value);
    triggerValidation(field);
  };

  useEffect(() => {

    props.formState.policies.forEach((policy, index) => {
      register({ name: `policies[${index}].insuredName` });
      setValue(`policies[${index}].insuredName`, policy.insuredName);

      register({ name: `policies[${index}].company` });
      setValue(`policies[${index}].company`, policy.company);

      register({ name: `policies[${index}].ownerName` });
      setValue(`policies[${index}].ownerName`, policy.ownerName);

      register({ name: `policies[${index}].insuranceDate` });
      setValue(`policies[${index}].insuranceDate`, policy.insuranceDate);

      register({ name: `policies[${index}].faceAmount` });
      setValue(`policies[${index}].faceAmount`, policy.faceAmount);

      register({ name: `policies[${index}].policyNumber` });
      setValue(`policies[${index}].policyNumber`, policy.policyNumber);

      register({ name: `policies[${index}].insurerName` });
      setValue(`policies[${index}].insurerName`, policy.insurerName);

    });

    register({ name: 'pendingApplications' });
    setValue('pendingApplications', props.formState.pendingApplications);

    register({ name: 'existingCoverage' });
    setValue('existingCoverage', props.formState.existingCoverage);

    register({ name: 'replaceExistingCoverage' });
    setValue('replaceExistingCoverage', props.formState.replaceExistingCoverage);

    register({ name: 'receiveComparativeInfoForm' });
    setValue('receiveComparativeInfoForm', props.formState.receiveComparativeInfoForm);

    register({ name: 'terminateExistingPolicy' });
    setValue('terminateExistingPolicy', props.formState.terminateExistingPolicy);

    register({ name: 'useExistingPolicyFunds' });
    setValue('useExistingPolicyFunds', props.formState.useExistingPolicyFunds);

    register({ name: 'state' });
    setValue('state', state);
  }, [register]);

  const addNewPolicy = () => {
    const newIndex = props.formState.policies.length;
    register({ name: `policies[${newIndex}].insuredName` });
    register({ name: `policies[${newIndex}].company` });
    register({ name: `policies[${newIndex}].ownerName` });
    register({ name: `policies[${newIndex}].insuranceDate` });
    register({ name: `policies[${newIndex}].faceAmount` });
    register({ name: `policies[${newIndex}].policyNumber` });
    register({ name: `policies[${newIndex}].insurerName` });
    const newForm = PolicyPurposeConfirmation.DefaultFormState().policies[0];
    props.onFieldChange('policies', [...props.formState.policies, newForm]);
  };

  const formPolicies = props.formState.policies.map((policyFormState, index) =>
    <PolicyInformation
      formState={policyFormState}
      onRemove={() => {
        props.onFieldChange(
          'policies',
          props.formState.policies.filter((form, formIndex) => formIndex !== index)
        );
        reset();
      }}
      onChange={(newForm) => {
        props.onFieldChange(
          'policies',
          props.formState.policies.map((form, formIndex) => {
            return formIndex === index ? newForm : form;
          })
        );
      }}
      setValue={setValue}
      trigger={triggerValidation}
      key={index}
      index={index}
      register={register}
      showPolicyRemove={props.formState.policies.length > 1}
      errors={errors.policies && errors.policies[index]}
      useExistingPolicyFunds={props.formState.useExistingPolicyFunds}
      state={state}
    />
  );

  return (
    <Container>
      <div className="formPage">
        <Row className="justify-content-center">
          <IconHolder icon={Step7Icon} viewBox="0 0 25 25" color='primary' />
        </Row>
        <h4 className="headerTitle">Confirming Your Existing Coverage</h4>
        <h5 className="headerSubtitle">Legal questions we're required to ask.</h5>

        <Row className="justify-content-center">
          <Col lg={5}>
            <Form.Group>
              <StyledRadioInput
                name="pendingApplications"
                id="pendingApplications"
                label="Does any Proposed Insured have life insurance or annuity applications pending with other companies?"
                value={props.formState.pendingApplications}
                options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                onChange={e => handleValueChange('pendingApplications', e)}
                errors={errors.pendingApplications}
              />
            </Form.Group>
            <Form.Group>
              <StyledRadioInput
                name="existingCoverage"
                id="existingCoverage"
                label="Is there any existing life insurance or annuity insurance coverage on the life of any Proposed Insured?"
                value={props.formState.existingCoverage}
                options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                onChange={e => handleValueChange('existingCoverage', e)}
                errors={errors.existingCoverage}
              />
            </Form.Group>
            {props.formState.existingCoverage === "true" && (
              <div>
                <Form.Group>
                  <TextInput
                    name="totalInsuredAmountExternal"
                    id="totalInsuredAmountExternal"
                    label="Total Death Benefit"
                    value={props.formState.totalInsuredAmountExternal}
                    onChange={e => props.onFieldChange('totalInsuredAmountExternal', e.target.value)}
                    InputProps={{
                      inputComponent: NumberFormatDollars,
                    }}
                    inputRef={register}
                    errors={errors.totalInsuredAmountExternal}
                  />
                </Form.Group>
                <Form.Group>
                  <StyledRadioInput
                    name="replaceExistingCoverage"
                    id="replaceExistingCoverage"
                    label="Will the insurance applied for replace or otherwise reduce in value any existing life insurance or annuity in force?"
                    value={props.formState.replaceExistingCoverage}
                    options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                    onChange={e => handleValueChange('replaceExistingCoverage', e)}
                    errors={errors.replaceExistingCoverage}
                  />
                </Form.Group>
                {props.formState.replaceExistingCoverage === "true" && (
                  <div className="mb-3">
                    {
                      props.showValidationMessage && (
                        <div className="text-danger mb-1">Replacement death benefit amount should not be greater than the total death benefit indicated above.</div>)
                    }
                    {formPolicies}
                    {props.formState.policies && props.formState.policies.length < 6 ? (
                      <Button color="primary" onClick={() => addNewPolicy()} className="d-flex m-auto">
                        ADD ANOTHER POLICY
                      </Button>) :
                      (
                        <div className="text-danger text-center">No additional policy may be added in the eApplication.</div>
                      )}
                  </div>
                )}
                {state === FLORIDA && (
                  <Form.Group>
                    <StyledRadioInput
                      name="receiveComparativeInfoForm"
                      id="receiveComparativeInfoForm"
                      label="Florida regulations give you the right to receive a written Comparative Info Form which summarizes your policy values. Indicate whether or not you wish a Comparative Information Form from the proposed company and your existing insurer or insurers."
                      value={props.formState.receiveComparativeInfoForm}
                      options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                      onChange={e => handleValueChange('receiveComparativeInfoForm', e)}
                      errors={errors.receiveComparativeInfoForm}
                    />
                  </Form.Group>
                )}

                {props.formState.replaceExistingCoverage === "true" && (
                  <div>
                    {state === MASSACHUSETTS && (
                      <Form.Group>
                        <CheckboxInput
                          name="yieldIndicesForCashValue"
                          id="yieldIndicesForCashValue"
                          checked={ConvertStringToBoolean(props.formState.yieldIndicesForCashValue)}
                          onChange={e => handleValueChange('yieldIndicesForCashValue', ConvertBooleanToString(e.target.checked))}
                          errors={errors.yieldIndicesForCashValue}
                          inputRef={register}
                          label="Check box to request yield indices for cash value policies"
                        />
                      </Form.Group>
                    )}
                    {!excludeReplacingReasonStates.includes(state) && (
                      <div>
                        <Form.Group>
                          <TextInput
                            name="reasonOfReplacement"
                            id="reasonOfReplacement"
                            label="Reason for Replacement"
                            value={props.formState.reasonOfReplacement}
                            onChange={e => props.onFieldChange('reasonOfReplacement', e.target.value)}
                            errors={errors.reasonOfReplacement}
                            inputRef={register}
                            multiline={true}
                            rows="5"
                            InputLabelProps={{ className: classes.multiLine }}
                          />
                        </Form.Group>
                      </div>
                    )}

                    <Row className="justify-content-center">
                      <Col lg={12}>
                        {(state === WYOMING) &&
                          <div>
                            <Card className="agreementCard">
                              <CardContent className="scrollbar">
                                <div className="overflow">
                                  <p className="agreementCardTextBold"><u>ITEMS TO CONSIDER</u></p>
                                  <ol className="agreementCardTextBold">
                                    <li>Due to a possible change insurability status (health, occupation, or high risk recreational activities) you might be denied new coverage or the premium may be higher than a standard premium.</li>
                                    <li>The Incontestability and Suicide Clause time periods would probably begin anew in a new policy. This could possibly result in a claim  being denied that might otherwise have been paid under an existing policy or contract.</li>
                                    <li>Your present insurance company may be able to modify your existing plan on terms which may be more favorable for you than  completely replacing it with a new policy or contract.</li>
                                    <li>Don’t terminate or alter your existing policy until after the new policy has been delivered to you and accepted by you.</li>
                                    <li>If you terminate your existing policy, you may incur surrender charges and/or penalties. The new policy you are purchasing may have a  new surrender charge period and/or sales charges.</li>
                                    <li>There may be tax consequences in replacing an existing policy. Is there a benefit from favorable “grandfathered” treatment of the old  policy under federal tax code? Is this transaction a “tax-free” exchange? See your tax advisor for specific tax advice regarding the  proposed transaction.</li>
                                    <li>Take your time in making your decision about purchasing the new policy. Gather all information about the new policy and compare it to  your old policy. Remember, you do not have to make a decision during the first meeting with your sales person.</li>
                                    <li> REMEMBER: Following receipt of a new life insurance policy or annuity contract you should immediately examine its contents. If you are  not satisfied with it for any reason, you have the right to return it within the thirty (30) day “examination period” to the insurer at its home  office or branch office or to the producer through whom it was purchased, for a full refund of premium. If you do return the policy or contract, you should request a dated receipt indicating that it was Returned.</li>
                                  </ol>
                                </div>
                              </CardContent>
                            </Card>
                            <Form.Group>
                              <CheckboxInput
                                name="ownerItemsToConsider"
                                id="ownerItemsToConsider"
                                checked={ConvertStringToBoolean(props.formState.ownerItemsToConsider)}
                                onChange={e => props.onFieldChange('ownerItemsToConsider', ConvertBooleanToString(e.target.checked))}
                                errors={errors.ownerItemsToConsider}
                                inputRef={register}
                                label={`I read the "Items to Consider"`}
                                className={classes.setMargin}
                                labelClassName={classes.setCheckBox}
                              />
                            </Form.Group>
                          </div>
                        }
                      </Col>
                    </Row>

                    {stateList.includes(state) && (
                      <div>
                        <Form.Group>
                          <StyledRadioInput
                            name="terminateExistingPolicy"
                            id="terminateExistingPolicy"
                            label="Are you considering discontinuing making premium payments, surrendering, forfeiting, assigning to the insurer, or otherwise terminating your existing policy or contract?"
                            value={props.formState.terminateExistingPolicy}
                            options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                            onChange={e => handleValueChange('terminateExistingPolicy', e)}
                            errors={errors.terminateExistingPolicy}
                          />
                        </Form.Group>
                        <Form.Group>
                          <StyledRadioInput
                            name="useExistingPolicyFunds"
                            id="useExistingPolicyFunds"
                            label="Are you considering using funds from your existing policies or contracts to pay premiums due on the new policy or contract?"
                            value={props.formState.useExistingPolicyFunds}
                            options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                            onChange={e => handleValueChange('useExistingPolicyFunds', e)}
                            errors={errors.useExistingPolicyFunds}
                          />
                        </Form.Group>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <Form.Group>
              <FormControl component="fieldset">
                <FormLabel component="legend" className={classes.labelFont}>Under penalties of perjury, I as the Owner certify that (check all that apply):</FormLabel>
                <Form.Group className="mb-0">
                  <CheckboxInput
                    name="usCitizen"
                    id="usCitizen"
                    checked={ConvertStringToBoolean(props.formState.usCitizen)}
                    onChange={e => props.onFieldChange('usCitizen', ConvertBooleanToString(e.target.checked))}
                    errors={errors.usCitizen}
                    inputRef={register}
                    label="I am a U.S. citizen or other U.S. person, and the number shown on this form is my correct taxpayer identification number (or I am waiting for a number to be issued to me), and,"
                    className={classes.setMargin}
                    labelClassName={classes.setCheckBox}
                  />
                </Form.Group>
                <Form.Group className="mb-0">
                  <CheckboxInput
                    name="backupWitholding"
                    id="backupWitholding"
                    checked={ConvertStringToBoolean(props.formState.backupWitholding)}
                    onChange={e => props.onFieldChange('backupWitholding', ConvertBooleanToString(e.target.checked))}
                    errors={errors.backupWitholding}
                    inputRef={register}
                    label="I am not subject to backup withholding because: (a) I am exempt from backup withholding, or (b) I have not been notified by the Internal Revenue Service (IRS) that I am subject to backup withholding as a result of a failure to report all interest or dividends, or (c) the IRS has notified me that I am no longer subject to backup withholding."
                    className={classes.setMargin}
                    labelClassName={classes.setCheckBox}
                  />
                </Form.Group>
              </FormControl>
            </Form.Group>
          </Col>
        </Row>
      </div>
    </Container>
  );
}

function PolicyInformation(props) {

  const onFieldChange = (field, value) => {
    props.onChange({ ...props.formState, [field]: value });
  };

  const handleValueChange = (field, value, longFieldName) => {
    props.setValue(longFieldName, value);
    onFieldChange(field, value);
    props.trigger(longFieldName);
  };

  return (
    <div>
      <Form.Group>
        <TextInput
          name="insuredName"
          id="insuredName"
          label="Insured's Name"
          value={props.formState.insuredName}
          onChange={e => handleValueChange('insuredName', e.target.value, `policies[${props.index}].insuredName`)}
          errors={props.errors && props.errors.insuredName}
          inputRef={props.register}
        />
      </Form.Group>
      <Form.Group>
        <SelectInput
          name="company"
          id="company"
          label="Company"
          value={props.formState.company}
          onChange={e => handleValueChange('company', e.target.value, `policies[${props.index}].company`)}
          errors={props.errors && props.errors.company}
          selectOptions={InsuranceProviders()}
          inputRef={props.register}
        />
      </Form.Group>
      {
        props.formState.company === "Other" &&
        <Form.Group>
          <TextInput
            name="insurerName"
            id="insurerName"
            label="Insurer Name"
            value={props.formState.insurerName}
            onChange={e => handleValueChange('insurerName', e.target.value, `policies[${props.index}].insurerName`)}
            errors={props.errors && props.errors.insurerName}
            inputRef={props.register}
          />
        </Form.Group>
      }
      <Form.Group>
        <TextInput
          name="policyNumber"
          id="policyNumber"
          label="Policy Number (or type 'Unknown')"
          value={props.formState.policyNumber}
          onChange={e => handleValueChange('policyNumber', e.target.value, `policies[${props.index}].policyNumber`)}
          errors={props.errors && props.errors.policyNumber}
          inputRef={props.register}
        />
      </Form.Group>
      <Form.Group>
        <TextInput
          name="ownerName"
          id="ownerName"
          label="Owner's Name"
          value={props.formState.ownerName}
          onChange={e => handleValueChange('ownerName', e.target.value, `policies[${props.index}].ownerName`)}
          errors={props.errors && props.errors.ownerName}
          inputRef={props.register}
        />
      </Form.Group>
      <Form.Group>
        <TextInput
          name="insuranceDate"
          id="insuranceDate"
          label="Policy Effective Date"
          value={props.formState.insuranceDate}
          errors={props.errors && props.errors.insuranceDate}
          inputRef={props.register}
          onChange={(e) => handleValueChange('insuranceDate', e.target.value, `policies[${props.index}].insuranceDate`)}
          InputProps={{
            inputComponent: InputMaskDate,
          }}
        />
      </Form.Group>

      <Form.Group>
        <TextInput
          name="faceAmount"
          id="faceAmount"
          label="Death Benefit"
          value={props.formState.faceAmount}
          onChange={(e) => handleValueChange('faceAmount', e.target.value, `policies[${props.index}].faceAmount`)}
          InputProps={{
            inputComponent: NumberFormatDollars,
          }}
          inputRef={props.register}
          errors={props.errors && props.errors.faceAmount}
        />
      </Form.Group>

      {props.showPolicyRemove &&
        <Form.Group>
          <Button onClick={props.onRemove}>Remove this Policy</Button>
        </Form.Group>
      }
    </div>
  );
}

PolicyPurposeConfirmation.propTypes = {
  formState: PropTypes.object.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  fullFormState: PropTypes.object,
  errors: PropTypes.object,
  register: PropTypes.func,
  setValue: PropTypes.func,
  triggerValidation: PropTypes.func,
  reset: PropTypes.func,
  showValidationMessage: PropTypes.bool,
};

PolicyInformation.propTypes = {
  formState: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.any,
  onRemove: PropTypes.func,
  showPolicyRemove: PropTypes.bool,
  trigger: PropTypes.func,
};

PolicyPurposeConfirmation.ValidationSchema = (birthdate) => {
  return object().shape({
    pendingApplications: string()
      .required('This field is required.'),
    existingCoverage: string().required('This field is required.'),
    usCitizen: string().required(),
    backupWitholding: string().required(),
    replaceExistingCoverage: string().when('existingCoverage', {
      is: "true",
      then: string().required('This field is required.'),
      otherwise: string().notRequired()
    }),
    terminateExistingPolicy: string().when(['existingCoverage', 'replaceExistingCoverage', 'state'], {
      is: (existingCoverage, replaceExistingCoverage, state) => existingCoverage === "true" && replaceExistingCoverage === "true" && stateList.includes(state),
      then: string().required('This field is required.'),
      otherwise: string().notRequired()
    }),
    useExistingPolicyFunds: string().when(['existingCoverage', 'replaceExistingCoverage', 'state'], {
      is: (existingCoverage, replaceExistingCoverage, state) => existingCoverage === "true" && replaceExistingCoverage === "true" && stateList.includes(state),
      then: string().when('terminateExistingPolicy', {
        is: "false",
        then: string().required('This field is required.').matches(/^true/, "Financial and Replacement questions are answered no, but replacement was indicated on this policy. Please resolve discrepancy."),
        otherwise: string().required('This field is required.')
      }),
      otherwise: string().notRequired()
    }),
    policies: mixed().when('existingCoverage', {
      is: "true",
      then: mixed().when('replaceExistingCoverage', {
        is: "true",
        then: yuparray(object().shape({
          insuredName: string().required('Insured name is required.'),
          company: string().required('Company is required.')
            .notOneOf(['Americo Financial', 'Great Southern'], 'Coverage is not available when replacing a policy from either Great Southern or Americo Financial.'),
          insurerName: string().when('company', {
            is: 'Other',
            then: string().required('Insurer Name is required.'),
            otherwise: string().notRequired()
          }),
          ownerName: string().required('Owner name is required.'),
          insuranceDate: string()
            .transform(emptyStringToUndefined)
            .typeError('Invalid date.')
            .test('validDate', 'Please enter a valid date.', (value) => isValid(new Date(value)))
            .test('dateTest', 'Please verify Policy Effect Date.', (value) => {
              if (!value) {
                return false;
              }
              const year = value.split('/')[2];
              if (parseInt(year, 10) < 1850) {
                return false;
              }

              if (isBefore(new Date(value), new Date(birthdate))) {
                return false;
              }

              if (isAfter(new Date(value), new Date())) {
                return false;
              }

              return true;
            })
            .required('Policy Effective Date is required.'),
          faceAmount: string().required('Death Benefit is required.'),
          replacement: string().notRequired(),
          policyNumber: string().required('Policy Number is required.'),
        })),
        otherwise: mixed().notRequired(),
      }),
      otherwise: mixed().notRequired(),
    }),
    ownerItemsToConsider: mixed().when(['replaceExistingCoverage', 'state'], {
      is: (replaceExistingCoverage, state) => replaceExistingCoverage === "true" && state === WYOMING,
      then: mixed().oneOf([true], 'You must read the Items to Consider.'),
      otherwise: mixed().notRequired(),
    }),

    yieldIndicesForCashValue: string().when(['replaceExistingCoverage', 'state'], {
      is: (replaceExistingCoverage, state) => replaceExistingCoverage === "true" && state === MASSACHUSETTS,
      then: string().required("This field is required."),
      otherwise: string().notRequired(),
    }),
    receiveComparativeInfoForm: string().when(['existingCoverage', 'state'], {
      is: (existingCoverage, state) => existingCoverage === "true" && state === FLORIDA,
      then: string().required("This field is required."),
      otherwise: string().notRequired(),
    }),
    reasonOfReplacement: string().when(['replaceExistingCoverage', 'state', 'existingCoverage'], {
      is: (replaceExistingCoverage, state, existingCoverage) => existingCoverage === "true" && replaceExistingCoverage === "true" && !excludeReplacingReasonStates.includes(state),
      then: string().required('Reason for replacement is required.').max(615, 'Response must be 615 characters or less.'),
      otherwise: string().notRequired()
    }),
    totalInsuredAmountExternal: string().when('existingCoverage', {
      is: "true",
      then: string().required('Total Death Benefit is required.'),
      otherwise: string().notRequired()
    }),
  });
};

PolicyPurposeConfirmation.DefaultFormState = (prefill = false) => {
  return {
    pendingApplications: prefill ? "false" : "",
    existingCoverage: prefill ? "false" : "",
    replaceExistingCoverage: prefill ? "false" : "",
    usCitizen: prefill ? "true" : "false",
    backupWitholding: prefill ? "true" : "false",
    terminateExistingPolicy: prefill ? "false" : "",
    useExistingPolicyFunds: prefill ? "false" : "",
    reasonOfReplacement: prefill ? "Test" : "",
    receiveComparativeInfoForm: prefill ? "false" : "",
    yieldIndicesForCashValue: "false",
    ownerItemsToConsider: prefill ? "true" : "",
    totalInsuredAmountExternal: prefill ? "" : "",
    policies: [
      {
        insuredName: prefill ? "Demo John" : "",
        company: prefill ? "Guardian Life" : "",
        ownerName: prefill ? "Demo Rhea" : "",
        insuranceDate: prefill ? "09/19/2000" : "",
        faceAmount: prefill ? "83702" : "",
        replacement: prefill ? "External" : "",
        policyNumber: prefill ? "123213123" : "",
        insurerName: prefill ? "Other" : ""
      }
    ]
  };
};



// WEBPACK FOOTER //
// ./src/components/Quoters/AmericoLoanProtection/steps/PolicyPurposeConfirmation.js